// lib/logicalCanvas/pluginRegistry.js
/**
 * Logical Canvas Plugin Registry
 * 
 * Defines all available systems and their capabilities.
 * This is the source of truth for what plugins can be enabled.
 * 
 * IMPORTANT: Capabilities are organized by SITE (Finance, Sales, CRM, Marketing, Security)
 * Sites define scope. Plugins define capabilities. Policies define enforcement.
 */

// Map system IDs to site IDs for scoping
const SYSTEM_TO_SITE_MAP = {
  "main_crm": "crm",
  "sales": "sales",
  "marketing": "marketing",
  "deltas": "finance",  // Finance owns change tracking
  "security": "security",
  "finance": "finance",  // Future finance-specific capabilities
};

export const SYSTEMS = [
  {
    systemId: "main_crm",
    systemName: "Main CRM",
    description: "Core customer relationship management capabilities",
    capabilities: [
      {
        id: "main_crm.quotes",
        label: "Quotes",
        description: "Create and manage sales quotes",
        sourceSystem: "main_crm",
        accessDefault: "read-write",
        modesAllowed: ["read-only", "read-write"],
      },
      {
        id: "main_crm.invoices",
        label: "Invoices",
        description: "Generate and track invoices",
        sourceSystem: "main_crm",
        accessDefault: "read-write",
        modesAllowed: ["read-only", "read-write"],
      },
      {
        id: "main_crm.payments",
        label: "Payments",
        description: "Process and record payments",
        sourceSystem: "main_crm",
        accessDefault: "read-write",
        modesAllowed: ["read-only", "read-write"],
      },
      {
        id: "main_crm.customers",
        label: "Customers",
        description: "Manage customer records and contacts",
        sourceSystem: "main_crm",
        accessDefault: "read-write",
        modesAllowed: ["read-only", "read-write"],
      },
    ],
  },
  {
    systemId: "sales",
    systemName: "Sales",
    description: "Sales pipeline and opportunity management",
    capabilities: [
      {
        id: "sales.pipeline",
        label: "Pipeline",
        description: "View and manage sales pipeline stages",
        sourceSystem: "sales",
        accessDefault: "read-only",
        modesAllowed: ["read-only", "read-write"],
      },
      {
        id: "sales.opportunities",
        label: "Opportunities",
        description: "Track sales opportunities and deals",
        sourceSystem: "sales",
        accessDefault: "read-only",
        modesAllowed: ["read-only", "read-write"],
      },
      {
        id: "sales.forecasting",
        label: "Forecasting",
        description: "Sales forecasting and projections",
        sourceSystem: "sales",
        accessDefault: "read-only",
        modesAllowed: ["read-only"],
      },
      {
        id: "sales.commissions",
        label: "Commissions",
        description: "Calculate and track sales commissions",
        sourceSystem: "sales",
        accessDefault: "read-only",
        modesAllowed: ["read-only", "read-write"],
      },
    ],
  },
  {
    systemId: "marketing",
    systemName: "Marketing",
    description: "Marketing campaigns and analytics",
    capabilities: [
      {
        id: "marketing.campaigns",
        label: "Campaigns",
        description: "Create and manage marketing campaigns",
        sourceSystem: "marketing",
        accessDefault: "read-only",
        modesAllowed: ["read-only", "read-write"],
      },
      {
        id: "marketing.leads",
        label: "Leads",
        description: "Track and qualify marketing leads",
        sourceSystem: "marketing",
        accessDefault: "read-only",
        modesAllowed: ["read-only", "read-write"],
      },
      {
        id: "marketing.analytics",
        label: "Analytics",
        description: "Marketing performance analytics",
        sourceSystem: "marketing",
        accessDefault: "read-only",
        modesAllowed: ["read-only"],
      },
    ],
  },
  {
    systemId: "deltas",
    systemName: "Deltas",
    description: "Change tracking and audit system",
    capabilities: [
      {
        id: "deltas.audit_log",
        label: "Audit Log",
        description: "View change history and audit trail",
        sourceSystem: "deltas",
        accessDefault: "read-only",
        modesAllowed: ["read-only"],
      },
      {
        id: "deltas.snapshots",
        label: "Snapshots",
        description: "Point-in-time data snapshots",
        sourceSystem: "deltas",
        accessDefault: "read-only",
        modesAllowed: ["read-only"],
      },
      {
        id: "deltas.rollback",
        label: "Rollback",
        description: "Restore previous versions",
        sourceSystem: "deltas",
        accessDefault: "disabled",
        modesAllowed: ["read-only"],
      },
    ],
  },
  {
    systemId: "security",
    systemName: "Security",
    description: "Security and compliance controls",
    capabilities: [
      {
        id: "security.access_control",
        label: "Access Control",
        description: "Manage user permissions and roles",
        sourceSystem: "security",
        accessDefault: "read-only",
        modesAllowed: ["read-only"],
      },
      {
        id: "security.encryption",
        label: "Encryption",
        description: "Data encryption settings",
        sourceSystem: "security",
        accessDefault: "read-only",
        modesAllowed: ["read-only"],
      },
      {
        id: "security.compliance",
        label: "Compliance",
        description: "Compliance reporting and controls",
        sourceSystem: "security",
        accessDefault: "read-only",
        modesAllowed: ["read-only"],
      },
    ],
  },
];

/**
 * Get all capabilities across all systems as a flat map
 * @returns {Map<string, object>} Map of capabilityId -> capability object
 */
export function getAllCapabilities() {
  const capMap = new Map();
  SYSTEMS.forEach(system => {
    system.capabilities.forEach(cap => {
      capMap.set(cap.id, { ...cap, systemName: system.systemName });
    });
  });
  return capMap;
}

/**
 * Get capabilities filtered by site
 * @param {string} siteId - The site to filter by (finance, sales, crm, marketing, security)
 * @returns {Map<string, object>} Map of capabilityId -> capability object for the site
 */
export function getCapabilitiesBySite(siteId) {
  const capMap = new Map();
  SYSTEMS.forEach(system => {
    const systemSite = SYSTEM_TO_SITE_MAP[system.systemId];
    if (systemSite === siteId) {
      system.capabilities.forEach(cap => {
        capMap.set(cap.id, { ...cap, systemName: system.systemName });
      });
    }
  });
  return capMap;
}

/**
 * Get capabilities for a specific system
 * @param {string} systemId 
 * @returns {Array} Array of capabilities
 */
export function getSystemCapabilities(systemId) {
  const system = SYSTEMS.find(s => s.systemId === systemId);
  return system ? system.capabilities : [];
}

/**
 * Get system by ID
 * @param {string} systemId 
 * @returns {object|null}
 */
export function getSystem(systemId) {
  return SYSTEMS.find(s => s.systemId === systemId) || null;
}

/**
 * Get all systems for a specific site
 * @param {string} siteId 
 * @returns {Array} Array of systems for the site
 */
export function getSystemsBySite(siteId) {
  return SYSTEMS.filter(system => SYSTEM_TO_SITE_MAP[system.systemId] === siteId);
}
